import React from 'react';
import { Modal, StyleSheet, Text, View } from 'react-native';
import { LoadingSpinner } from './LoadingSpinner';
import { useTheme } from '../../context/ThemeContext';
import { BorderRadius, FontSize, Spacing } from '../../theme/colors';

interface SubmittingOverlayProps {
  visible: boolean;
  title: string;
  /** What is happening and what the user should (not) do meanwhile. */
  message?: string;
}

/**
 * A blocking "please wait" for a submission in flight. Rendered through
 * Modal so it is portaled to document.body: it then covers the whole viewport
 * and stacks above any popup the submit was confirmed from - an overlay laid
 * inside the page shell sits beneath such a popup and scrolls with the page.
 * The Modal takes no onRequestClose action on purpose: the wait cannot be
 * dismissed, only finished.
 */
export function SubmittingOverlay({ visible, title, message }: SubmittingOverlayProps) {
  const { colors } = useTheme();
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => undefined}>
      <View style={styles.backdrop} accessibilityRole="progressbar" accessibilityLabel={title} accessibilityLiveRegion="polite">
        <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <LoadingSpinner size="large" />
          <Text style={[styles.title, { color: colors.textPrimary }]}>{title}</Text>
          {message ? <Text style={[styles.message, { color: colors.textSecondary }]}>{message}</Text> : null}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    alignItems: 'center',
    paddingVertical: Spacing.lg,
    paddingHorizontal: Spacing.xl,
    borderRadius: BorderRadius.lg,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.3,
    shadowRadius: 24,
    elevation: 10,
  },
  title: {
    fontSize: FontSize.md,
    fontWeight: '700',
    textAlign: 'center',
  },
  message: {
    marginTop: 6,
    fontSize: FontSize.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
});
